import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useConfirm } from "../components/ConfirmDialog";
import { Plus, Pencil, KeyRound, Trash2, ShieldCheck, Power, ArrowLeft, UserPlus2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "../lib/format";

const ROLE_LABELS = {
  admin: "Administrateur",
  operator: "Opérateur",
  field_operator: "Tournée distributeurs",
};

export default function Users() {
  const { user: currentUser } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (u) => {
    const ok = await confirm({
      title: "Supprimer ce compte ?",
      description: `Le compte "${u.email}" sera définitivement supprimé. Cette action est irréversible.`,
      confirmText: "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Compte supprimé");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  const onToggleDisabled = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { disabled: !u.disabled });
      toast.success(u.disabled ? "Compte réactivé" : "Compte désactivé");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link
            to="/parametres"
            className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57] flex items-center gap-1.5 hover:text-[#1A1C1A] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Paramètres
          </Link>
          <h1 className="font-heading text-2xl font-medium text-[#1A1C1A] mt-1 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-[#1F4E3D]" /> Comptes utilisateurs
          </h1>
          <p className="text-sm text-[#575A57] mt-1">
            Gérez les accès admin et opérateurs à AgroManage.
          </p>
        </div>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-user-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau compte
            </Button>
          </DialogTrigger>
          <UserCreateDialog
            onClose={() => setCreateOpen(false)}
            onCreated={() => { setCreateOpen(false); load(); }}
          />
        </Dialog>
      </div>

      {/* Légende */}
      <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded-md p-3 text-xs text-[#575A57] flex items-start gap-2">
        <UserPlus2 className="w-4 h-4 mt-0.5 text-[#1F4E3D] shrink-0" />
        <div>
          <strong className="text-[#1A1C1A]">Rôles disponibles :</strong>
          <span className="ml-1.5"><Badge variant="outline" className="bg-[#E8EEE9] border-[#1F4E3D]/30 text-[#1F4E3D] text-[10px]">Administrateur</Badge> accède à tout.</span>
          <span className="ml-1.5"><Badge variant="outline" className="bg-[#EEF2F7] border-[#3B5B7A]/30 text-[#3B5B7A] text-[10px]">Opérateur</Badge> accède aux Stocks, Ventes, Étiquettes et Traçabilité (lecture seule sur les produits).</span>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F9F6] text-xs uppercase tracking-wider text-[#575A57]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Email</th>
              <th className="text-left px-4 py-3 font-semibold">Nom</th>
              <th className="text-left px-4 py-3 font-semibold">Rôle</th>
              <th className="text-left px-4 py-3 font-semibold">Statut</th>
              <th className="text-left px-4 py-3 font-semibold">Créé le</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#A3A6A3]">Chargement…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#A3A6A3]">Aucun compte</td></tr>
            ) : users.map((u) => {
              const isSelf = u.id === currentUser?.id;
              return (
                <tr key={u.id} className="border-t border-[#F0F0EB] hover:bg-[#FAFAF7]" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium text-[#1A1C1A]">{u.email}{isSelf && <span className="ml-1.5 text-[10px] uppercase text-[#1F4E3D] font-semibold">(vous)</span>}</td>
                  <td className="px-4 py-3 text-[#3A3A3A]">{u.name || "—"}</td>
                  <td className="px-4 py-3">
                    {u.role === "admin" ? (
                      <Badge variant="outline" className="bg-[#E8EEE9] border-[#1F4E3D]/30 text-[#1F4E3D]">Admin</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-[#EEF2F7] border-[#3B5B7A]/30 text-[#3B5B7A]">Opérateur</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.disabled ? (
                      <Badge variant="outline" className="bg-[#FCEEEA] border-[#C05C47]/30 text-[#C05C47]">Désactivé</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-[#F0F0EB] border-[#A3A6A3]/30 text-[#3A3A3A]">Actif</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[#575A57]">{fmtDateTime(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setEditingUser(u)}
                        className="p-1.5 rounded hover:bg-[#F0F0EB] text-[#575A57] hover:text-[#1A1C1A]"
                        title="Modifier"
                        data-testid={`edit-user-${u.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setResetUser(u)}
                        className="p-1.5 rounded hover:bg-[#F0F0EB] text-[#575A57] hover:text-[#1A1C1A]"
                        title="Réinitialiser le mot de passe"
                        data-testid={`reset-user-${u.id}`}
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      {!isSelf && (
                        <button
                          onClick={() => onToggleDisabled(u)}
                          className="p-1.5 rounded hover:bg-[#F0F0EB] text-[#575A57] hover:text-[#1A1C1A]"
                          title={u.disabled ? "Réactiver" : "Désactiver"}
                          data-testid={`toggle-user-${u.id}`}
                        >
                          <Power className="w-4 h-4" />
                        </button>
                      )}
                      {!isSelf && (
                        <button
                          onClick={() => onDelete(u)}
                          className="p-1.5 rounded hover:bg-[#FCEEEA] text-[#575A57] hover:text-[#C05C47]"
                          title="Supprimer"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editingUser} onOpenChange={(v) => !v && setEditingUser(null)}>
        {editingUser && (
          <UserEditDialog
            user={editingUser}
            isSelf={editingUser.id === currentUser?.id}
            onClose={() => setEditingUser(null)}
            onSaved={() => { setEditingUser(null); load(); }}
          />
        )}
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(v) => !v && setResetUser(null)}>
        {resetUser && (
          <PasswordResetDialog
            user={resetUser}
            onClose={() => setResetUser(null)}
            onDone={() => setResetUser(null)}
          />
        )}
      </Dialog>
    </div>
  );
}

function UserCreateDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "operator" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/users", form);
      toast.success("Compte créé");
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de création");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-md" data-testid="create-user-dialog">
      <DialogHeader>
        <DialogTitle>Nouveau compte</DialogTitle>
        <DialogDescription>
          Remettez le mot de passe à l'utilisateur en personne ou par un canal sécurisé.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4 mt-2">
        <div>
          <Label className="mb-1.5 block">Adresse email</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="prenom@maison-thai.pro"
            required
            data-testid="create-email"
          />
        </div>
        <div>
          <Label className="mb-1.5 block">Nom (facultatif)</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Alice"
            data-testid="create-name"
          />
        </div>
        <div>
          <Label className="mb-1.5 block">Mot de passe initial</Label>
          <Input
            type="text"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="≥ 8 caractères"
            required
            minLength={8}
            data-testid="create-password"
          />
          <p className="text-xs text-[#575A57] mt-1">
            Minimum 8 caractères. Communiquez-le à l'utilisateur, il pourra le changer ensuite via vous.
          </p>
        </div>
        <div>
          <Label className="mb-1.5 block">Rôle</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="create-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="operator">Opérateur — Stocks, Ventes, Étiquettes</SelectItem>
              <SelectItem value="field_operator">Tournée — Distributeurs, restock, scan (terrain)</SelectItem>
              <SelectItem value="driver">Livreur — Module Tournée UNIQUEMENT (aucun accès AM)</SelectItem>
              <SelectItem value="admin">Administrateur — Accès complet</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter className="gap-2 mt-6">
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit" disabled={submitting} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-submit">
            {submitting ? "Création…" : "Créer le compte"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function UserEditDialog({ user, isSelf, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name || "", role: user.role });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/users/${user.id}`, form);
      toast.success("Compte modifié");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-md" data-testid="edit-user-dialog">
      <DialogHeader>
        <DialogTitle>Modifier le compte</DialogTitle>
        <DialogDescription className="font-mono text-xs">{user.email}</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4 mt-2">
        <div>
          <Label className="mb-1.5 block">Nom</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="edit-name"
          />
        </div>
        <div>
          <Label className="mb-1.5 block">Rôle</Label>
          <Select
            value={form.role}
            onValueChange={(v) => setForm({ ...form, role: v })}
            disabled={isSelf}
          >
            <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="operator">Opérateur</SelectItem>
              <SelectItem value="field_operator">Tournée distributeurs</SelectItem>
              <SelectItem value="driver">Livreur (tournée uniquement)</SelectItem>
              <SelectItem value="admin">Administrateur</SelectItem>
            </SelectContent>
          </Select>
          {isSelf && (
            <p className="text-xs text-[#8C6B2F] mt-1.5">Vous ne pouvez pas modifier votre propre rôle.</p>
          )}
        </div>
        <DialogFooter className="gap-2 mt-6">
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit" disabled={submitting} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="edit-submit">
            {submitting ? "…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function PasswordResetDialog({ user, onClose, onDone }) {
  const [pwd, setPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/users/${user.id}/password`, { password: pwd });
      toast.success("Mot de passe réinitialisé. Communiquez-le à l'utilisateur.");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-md" data-testid="reset-pwd-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-[#1F4E3D]" /> Réinitialiser le mot de passe
        </DialogTitle>
        <DialogDescription>
          Le compte <span className="font-mono">{user.email}</span> recevra ce nouveau mot de passe immédiatement. Communiquez-le ensuite à l'utilisateur.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4 mt-2">
        <div>
          <Label className="mb-1.5 block">Nouveau mot de passe</Label>
          <Input
            type="text"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="≥ 8 caractères"
            required
            minLength={8}
            data-testid="reset-password"
            autoFocus
          />
        </div>
        <DialogFooter className="gap-2 mt-6">
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit" disabled={submitting || pwd.length < 8} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="reset-submit">
            {submitting ? "…" : "Réinitialiser"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
